import { Banner, Button, LoadingSpinner, Logo } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import {
  consoleApi,
  getAccessToken,
  getApiError,
  getErrorMessage,
  getTokenForConnectionAndEmail,
  login,
  logout,
  useNotificationActions,
} from '@nx/state';
import { isNonEmptyString, isNotNullish, isNullish } from '@nx/stdlib';
import { Center } from '@nx/ui';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import { isString } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';

const logger = createLogger(APP_SCOPE.aura);

const isRecord = (data: unknown): data is Record<string, unknown> => {
  return isNotNullish(data) && typeof data === 'object';
};

const LinkAccounts = ({
  toConnection,
  fromConnection,
  fromSubPrefix,
  email,
  returnTo,
}: {
  /**
   * The connection we are linking to
   */
  toConnection: string;
  /**
   * The connection we are linking from. IE
   * the conneciton the user should be logged into when doing the
   * linking
   */
  fromConnection?: string | null;

  /**
   * The sub prefix associated with the connection. This
   * is not always the same as the connection
   */
  fromSubPrefix?: string | null;

  /**
   * The email we are linking
   */
  email: string;

  returnTo?: string | null;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitLoading, setIsInitLoading] = useState(true);
  const [linkLogins, linkLoginsRes] = consoleApi.useLinkLoginsMutation();
  const { data: currentUser, isLoading: isGetUserLoading, error: getUserError } = consoleApi.useGetUserDetailsQuery();
  const notifications = useNotificationActions();
  const location = useLocation();

  const handleLink = async () => {
    setIsLoading(true);
    let token;
    const currentBearerToken = await getAccessToken();
    try {
      token = await getTokenForConnectionAndEmail(toConnection, email);
    } catch (e) {
      logger.error(e);
      setIsLoading(false);
      notifications.addNotification({
        title: 'Error',
        description: 'Failed to link accounts',
        timeout: 5000,
        type: 'danger',
      });
      return;
    }
    if (isNullish(currentBearerToken) || isNullish(token)) {
      return;
    }

    linkLogins({ currentBearerToken, oldAccountToken: token })
      .unwrap()
      .then(() => {
        let url = new URL(window.location.href);
        url.pathname = '/';
        if (isNotNullish(returnTo) && isNonEmptyString(returnTo)) {
          url = new URL(window.location.origin + returnTo);
        }

        // Navigate to home after linking with a full refresh of the page
        window.location.replace(url);
      })
      .catch((e: FetchBaseQueryError | SerializedError | undefined) => {
        const error = getApiError(e);
        const message = getErrorMessage(error);
        logger.error(message);
        notifications.addNotification({
          title: 'Error',
          description: 'Failed to link accounts',
          timeout: 5000,
          type: 'danger',
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleLogout = () => {
    logout();
  };

  useEffect(() => {
    const handler = async () => {
      if (isGetUserLoading) {
        return;
      }

      const errorEmail = isNotNullish(getUserError) ? getApiError(getUserError).extra?.email : undefined;
      const currentUserEmail = currentUser?.email ?? (isString(errorEmail) ? errorEmail : undefined);

      if (isNullish(currentUserEmail)) {
        logger.error('[linkAccounts] missing current user email');
        setIsInitLoading(false);
        return;
      }

      const currentBearerToken = await getAccessToken();
      if (isNullish(currentBearerToken)) {
        logger.error('[linkAccounts] missing current bearer token');
        setIsInitLoading(false);
        return;
      }

      const { sub } = jwtDecode<JwtPayload>(currentBearerToken, {
        header: false,
      });
      if (isNullish(sub)) {
        logger.error('[linkAccounts] token missing sub claim');
        setIsInitLoading(false);
        return;
      }

      if (isNullish(email)) {
        logger.error('[linkAccounts] token missing email claim');
        setIsInitLoading(false);
        return;
      }

      const [subPrefix] = sub.split('|');
      const subsMatch = isNullish(fromSubPrefix) ? true : subPrefix === fromSubPrefix;
      const emailsMatch = currentUserEmail === email;
      if (!subsMatch || !emailsMatch) {
        if (!subsMatch) {
          logger.info(
            `[linkAccounts] sub prefix ${subPrefix} does not match expected prefix ${fromSubPrefix}. Logging in with connection ${fromConnection}...`,
          );
        } else {
          logger.info(
            `[linkAccounts] email ${currentUserEmail} does not match expected email ${email}. Logging in with email ${email}...`,
          );
        }
        await login(
          undefined,
          {
            login_hint: email,
            // This prompt is important as itll make the login screen
            // always show even if theres an existing session
            prompt: 'login',
            extraQueryParams:
              !subsMatch && isNotNullish(fromConnection)
                ? {
                    connection: fromConnection,
                  }
                : {},
            state: {
              returnTo: location.pathname + location.search,
            },
          },
          true,
        );
      } else {
        if (isNotNullish(fromSubPrefix)) {
          logger.info(
            `[linkAccounts] sub prefix ${subPrefix} matches expected prefix ${fromSubPrefix} and email ${currentUserEmail} matches expected email ${email}`,
          );
        } else {
          logger.info(`[linkAccounts] email ${currentUserEmail} matches expected email ${email}`);
        }
        setIsInitLoading(false);
      }
    };

    void handler();
  }, [
    email,
    fromConnection,
    fromSubPrefix,
    location.pathname,
    location.search,
    isGetUserLoading,
    currentUser,
    getUserError,
  ]);

  const error = getApiError(linkLoginsRes.error);

  if (isInitLoading) {
    return (
      <Center>
        <LoadingSpinner size="large" />
      </Center>
    );
  }

  return (
    <div className="flex grow flex-col">
      <div className="p-2">
        <Logo width={100} height={32} />
      </div>
      <div className="mt-12 flex  justify-center">
        <div className="bg-neutral-bg-weak shadow-raised flex max-w-md flex-col gap-4 rounded-md p-8">
          <h3>Link Accounts</h3>
          <p>
            There is already an account using the email address <code>{email}</code>.
          </p>
          <p>
            Link your accounts by logging in using your previous login method via the <b>Link</b> button below.
          </p>
          <div className="flex flex-nowrap gap-3">
            <Button onClick={handleLogout} fill="outlined">
              Logout
            </Button>
            <Button
              onClick={() => {
                void handleLink();
              }}
              isLoading={isLoading}
              htmlAttributes={{
                'data-testid': 'link-button',
              }}
            >
              Link
            </Button>
          </div>
          {linkLoginsRes.isError && (
            <Banner
              type="danger"
              title={`Failed to authenticate: ${error.reason}`}
              description={error.message}
              usage="inline"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const LinkAccountsPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { state } = location;
  const toConnection = isRecord(state) ? state.connection : searchParams.get('connection');
  const email = isRecord(state) ? state.email : searchParams.get('email');
  const fromConnection = searchParams.get('from_connection');
  const fromSubPrefix = searchParams.get('from_sub_prefix');
  const returnTo = searchParams.get('return_to');

  if (isNullish(toConnection) || typeof toConnection !== 'string' || isNullish(email) || typeof email !== 'string') {
    return (
      <div className="m-auto mt-12 flex justify-center">
        <Banner
          type="danger"
          title="Something went wrong"
          description="Email and/or connection are invalid or missing."
          usage="inline"
        />
      </div>
    );
  }

  return (
    <LinkAccounts
      email={email}
      toConnection={toConnection}
      fromConnection={fromConnection}
      fromSubPrefix={fromSubPrefix}
      returnTo={returnTo}
    />
  );
};
